import * as React from 'react';
import styles from './App.module.css';
import {Config, Hooks, Contexts, Components, Types, TrackingAPI, Utils} from '@trendads/common';
import cx from 'classnames';
import CONFIG, {resolvePath} from './Config';

export const LazyConfigurator = React.lazy(() => import('@trendads/common/dist/components/Configurator'));
export const LazyOverview = React.lazy(() => import('@trendads/common/dist/components/Overview'));

const Templates = Config.buildTemplates(CONFIG, resolvePath);

interface TrackingWrapperProps {
    apiHost: string;
    affiliateId: string | undefined;
    trackingEnabled: boolean;
    visible: boolean;
}

const TrackingWrapper: React.FC<TrackingWrapperProps> = ({
    children,
    apiHost,
    affiliateId,
    trackingEnabled,
    visible
}) => {
    const hasTrackedView = React.useRef(false);

    React.useEffect(() => {
        if (!trackingEnabled || !affiliateId || !visible) return;
        const unsubscribe = Utils.TCF.subscribe('REST_API_Tracking', ({allowed}) => {
            if (allowed && !hasTrackedView.current) {
                TrackingAPI.trackView(apiHost, affiliateId, '');
                hasTrackedView.current = true;
            }
        });
        return () => unsubscribe();
    }, [apiHost, affiliateId, trackingEnabled, visible]);

    return <>{children}</>;
};

const App: React.FC = () => {
    const {loading, templateParams, requestParams, configuratorParams, visible} = Hooks.useInit(
        CONFIG,
        ({match, deeplinks, campaignId, campaignLink, requestParams, encodeDeepLink}) => {
            const url = new URL(CONFIG.linkTarget + `/${requestParams.language}`);

            if (campaignLink) {
                url.pathname = campaignLink;
            }

            let bannerSource;
            try {
                bannerSource =
                    window.location.hostname !== window.top?.location.hostname
                        ? document.referrer
                        : window.top.location.hostname;
            } catch (err) {
                bannerSource = document.referrer;
            }
            url.searchParams.set('utm_source', bannerSource);

            if (match) {
                url.searchParams.set('contestId', match.id);
            }

            if (campaignId) {
                url.searchParams.set('utm_content', campaignId);
            }

            if (requestParams.affiliateId) {
                url.searchParams.set('a', requestParams.affiliateId);
                url.searchParams.set('utm_campaign', requestParams.affiliateId);
            }

            if (deeplinks && deeplinks.length > 0) {
                url.searchParams.append('betslip', 'compact');
                url.searchParams.append('contestId', deeplinks[0].contestId);
                url.searchParams.append('mo', deeplinks.length === 1 ? 'Single' : 'Combi');
                url.searchParams.append('st', String(requestParams.stake));

                deeplinks.forEach(deeplink => {
                    url.searchParams.append(
                        'se',
                        `${deeplink.contestId}!${deeplink.bettingTypeKey}!${deeplink.outcomeKey}`
                    );
                });
            }

            return encodeDeepLink(url.toString());
        }
    );

    if (loading) return null;

    if (requestParams?.template !== undefined && requestParams?.size !== undefined && templateParams) {
        if (requestParams?.overview) {
            return (
                <React.Suspense fallback={null}>
                    <LazyOverview config={CONFIG} requestParams={requestParams} />
                </React.Suspense>
            );
        } else {
            const Template =
                requestParams.template && requestParams.size
                    ? Templates[requestParams.template + requestParams.size]
                    : null;

            if (Template) {
                return (
                    <div
                        className={cx(
                            styles.App,
                            requestParams!.size === Types.Size.API ? styles.apiSize : '',
                            `license-${templateParams.settings.license.toLowerCase()}`
                        )}>
                        <Contexts.DebugMode.Provider value={requestParams.debug === 'true'}>
                            <Components.ErrorBoundary fallback={<>Template not available</>}>
                                <React.Suspense fallback={null}>
                                    <TrackingWrapper
                                        apiHost={CONFIG.apiHost}
                                        affiliateId={requestParams.affiliateId}
                                        trackingEnabled={CONFIG.trackingEnabled}
                                        visible={visible}>
                                        <Template {...templateParams} />
                                    </TrackingWrapper>
                                </React.Suspense>
                            </Components.ErrorBoundary>
                        </Contexts.DebugMode.Provider>
                    </div>
                );
            } else {
                return <div>Invalid template</div>;
            }
        }
    }

    if (configuratorParams) {
        return (
            <React.Suspense fallback={null}>
                <LazyConfigurator {...configuratorParams} />
            </React.Suspense>
        );
    }

    return null;
};

export default App;
